import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconTypesKeys, Dropdown, Icon } from "@evergis/ui";
import { GeometryType, ServiceInfoDc } from "@evergis/api";

import { ResourceMeta } from "../../../../DialogComponents/ResourceMeta/ResourceMeta";
import { DataField, FieldValue } from "../../../../styled";
import { BlackIcon } from "../../../../EditResource/components/Table/styled";
import { ProjectMetaBlock, RestrictedWidthContainer } from "../../../../DialogComponents/styled";
import { LinearProgress, LoadingDescription } from "../../../../BulkCopy/styled";
import { TransferIconWrapper } from "../styled";

import { getGeometryType } from "../../../../../../evergis/core/attributes/geometryTypes";
import { formatNumber } from "../../../../../../utils/formatNumber";
import { useLayerDetails } from "../hooks/useLayerDetails";
import { useRowsCount } from "../hooks/useRowsCount";

import { Step0Props } from "../types";
import { LayerInfo } from "../../../../../../interfaces/resourceInfo";

export const Step0: FC<Step0Props> = ({
  layerInfo,
  onTargetChange,
  projectLayersLoading,
  allLayersLoading,
  filteredLayers,
}) => {
  const { t } = useTranslation();
  const { meta: sourceMeta, geometryType: sourceGeometry } = useLayerDetails(layerInfo as LayerInfo);
  const [selectedTarget, setSelectedTarget] = useState<ServiceInfoDc | undefined>();
  const { meta: targetMeta } = useLayerDetails(selectedTarget as LayerInfo);
  const { name: targetName, description: targetDescription, geometryType: targetGeometry } = selectedTarget || {};
  const rowCount = useRowsCount(layerInfo);

  useEffect(() => {
    setSelectedTarget(filteredLayers[0]);
  }, [filteredLayers]);

  useEffect(() => {
    onTargetChange(selectedTarget);
  }, [onTargetChange, selectedTarget]);

  return projectLayersLoading || allLayersLoading ? (
    <RestrictedWidthContainer>
      <LoadingDescription>{t("copyResource.gettingLayersData", { ns: "dialogs" })}</LoadingDescription>
      <LinearProgress />
    </RestrictedWidthContainer>
  ) : (
    <RestrictedWidthContainer>
      <ProjectMetaBlock>
        <ResourceMeta meta={sourceMeta} readOnly noMeta>
          <div>
            <DataField>
              <FieldValue field={t("copyResource.layerToCopy", { ns: "dialogs" })} value={sourceMeta.alias} />
            </DataField>
            <DataField style={{ marginTop: "1rem" }}>
              <FieldValue
                field={t("copyResource.layerDescription", { ns: "dialogs" })}
                value={sourceMeta.description}
              />
            </DataField>
          </div>
          <DataField>
            <FieldValue
              field={t("copyResource.geometryType", { ns: "dialogs" })}
              value={
                <>
                  {sourceGeometry && (
                    <BlackIcon kind={`geom_${sourceGeometry}` as IconTypesKeys} style={{ marginRight: "0.5rem" }} />
                  )}
                  <span>{t(getGeometryType(sourceGeometry as GeometryType), { ns: "common" })}</span>
                </>
              }
            />
            <FieldValue field={t("copyResource.featuresToCopy", { ns: "dialogs" })} value={formatNumber(rowCount)} />
          </DataField>
        </ResourceMeta>
      </ProjectMetaBlock>
      <TransferIconWrapper>
        <Icon kind="arrow_back" />
      </TransferIconWrapper>
      <ProjectMetaBlock>
        <ResourceMeta meta={targetMeta} readOnly noMeta>
          <div>
            <DataField>
              <FieldValue
                field={t("copyResource.targetLayer", { ns: "dialogs" })}
                value={
                  <Dropdown
                    zIndex={9999}
                    width="31.25rem"
                    value={targetName}
                    onChange={value => setSelectedTarget(filteredLayers.find(item => item.name === value[0].value))}
                    options={filteredLayers.map(item => ({
                      value: item.name,
                      text: item.alias,
                    }))}
                  />
                }
              />
            </DataField>
            <DataField style={{ marginTop: "1rem" }}>
              <FieldValue
                field={t("copyResource.layerDescription", { ns: "dialogs" })}
                value={targetDescription || ""}
              />
            </DataField>
          </div>
          <DataField>
            <FieldValue
              field={t("copyResource.geometryType", { ns: "dialogs" })}
              value={
                <>
                  {selectedTarget?.geometryType && (
                    <BlackIcon kind={`geom_${targetGeometry}` as IconTypesKeys} style={{ marginRight: "0.5rem" }} />
                  )}
                  <span>{t(getGeometryType(targetGeometry), { ns: "common" })}</span>
                </>
              }
            />
            <FieldValue
              field={t("copyResource.totalFeatures", { ns: "dialogs" })}
              value={formatNumber(selectedTarget?.objectCount || 0)}
            />
          </DataField>
        </ResourceMeta>
      </ProjectMetaBlock>
    </RestrictedWidthContainer>
  );
};
